/* tslint:disable:max-line-length */
import {Injectable} from '@angular/core';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {firstValueFrom, lastValueFrom, NEVER, Observable} from 'rxjs';
import {BasedataService} from '../shared/basedata.service';
import {Attachment, AttachmentData} from './attachment';
import {ConfigService} from '@docma-mm-core/config/config.service';
import {catchError, map, takeUntil} from 'rxjs/operators';
import {GroupTypeEnum} from '@docma-mm-core/group/group-type.enum';
import {parseContentDispositionFilename} from '@mydocma/task';
import {ToId} from '@docma-mm-core/api.functions';

@Injectable()
export class AttachmentApiService extends BasedataService {


  private structureUrl = '/structures/';
  private faultUrl = '/faults/';
  private activityUrl = '/activities/';
  private groupUrl = '/groups/';
  private attachmentUrl = '/attachments';

  constructor(private readonly httpClient: HttpClient,
              configService: ConfigService) {
    super(configService);
  }

  private getStructureAttachmentUrl(structureId: number) {
    return super.getFullUrl(this.structureUrl + structureId + this.attachmentUrl);
  }

  private getFaultAttachmentUrl(faultUId: string) {
    const id = ToId(faultUId);
    return super.getFullUrl(this.faultUrl + id + this.attachmentUrl);
  }

  private getActivityAttachmentUrl(activityUId: string) {
    const id = ToId(activityUId);
    return super.getFullUrl(this.activityUrl + id + this.attachmentUrl);
  }

  private getGroupAttachmentUrl(groupUid: string) {
    return super.getFullUrl(this.groupUrl + 'uniqueId.' + groupUid + this.attachmentUrl);
  }
  private getAttachmentUrl() {
    return super.getFullUrl(this.attachmentUrl);
  }

  // get attachments info by structure
  getStructureAttachments(structureId: number): Observable<AttachmentData[]> {
    const endpoint = this.getStructureAttachmentUrl(structureId);
    return this.httpClient
      .get<AttachmentData[]>(endpoint, { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get attachments info by structure
  getStructurePlans(structureId: number): Observable<AttachmentData[]> {
    const endpoint = this.getStructureAttachmentUrl(structureId);

    let httpParams = new HttpParams();
    httpParams = httpParams.set('isActive', 'true');
    httpParams = httpParams.set('isEnabledOnMobile', 'true');
    httpParams = httpParams.set('isLocalizable', 'true');

    return this.httpClient
      .get<AttachmentData[]>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  async getAttachmentData(attachmentUid: string) {
    const uid = ToId(attachmentUid);

    const endpoint = `${super.getFullUrl(this.attachmentUrl)}/${uid}/metadata`;
    const loaded = await firstValueFrom(this.httpClient.get<AttachmentData>(endpoint, { headers: this.getHeaders() })
        .pipe(
            map( at => {
                return new AttachmentData(at);
            }),
            catchError((e) => this.handleError(e))
        ));

    return loaded;
  }

  // get attachment by id, height, width
  async getAttachment(attachmentId: number, extendedContentDisposition: boolean = false, width: number = 0, height: number = 0, originalWithScribbles: boolean = false, cancelation?: Observable<any>): Promise<File> {
    const endpoint = this.getAttachmentUrl() + '/' + attachmentId;
    let fileToDownload: File;

    let httpParams = new HttpParams();
    httpParams = httpParams.set('height', height.toString());
    httpParams = httpParams.set('width', width.toString());
    httpParams = httpParams.set('extendedContentDisposition', extendedContentDisposition);
    httpParams = httpParams.set('originalWithScribbles', originalWithScribbles);

    try {
      fileToDownload = await lastValueFrom(
        this.httpClient.get<File>(endpoint, {observe: 'response', headers: this.getHeaders(), params: httpParams, responseType: 'blob' as 'json'})
          .pipe(
            takeUntil(cancelation || NEVER),
            map((x: any) => {
              const r: HttpResponse<File> = x as HttpResponse<File>;

              let contentDisposition: string = null;
              if (r.headers.has('content-disposition')) {
                contentDisposition = r.headers.get('content-disposition');
              }

              if (contentDisposition) {
                const filename = parseContentDispositionFilename(contentDisposition);
                if (contentDisposition && filename) {
                  return new File([r.body], filename, {type: r.body.type});
                }
              }
              return r.body;
            })
          ));

      return fileToDownload;
    }
    catch (er) {
      // this.notificationService.error(`Can't download file from ${targetUrl}`);
      throw er;
    }
  }

  // upload attachment to activity
  addAttachmentToActivity(activityUId: string, attachmentData: AttachmentData, file: File, forceNoThumbnail: boolean = false): Observable<AttachmentData> {
    const endpoint = this.getActivityAttachmentUrl(activityUId);
    const formData = this.createFormData(attachmentData, file);

    let httpParams = new HttpParams();
    httpParams = httpParams.set('forceNoThumbnail', forceNoThumbnail);

    return this.httpClient.post<any>(endpoint, formData, { headers: this.getHeaders(true), observe: 'response', params: httpParams })
    .pipe(
        map( response => {
          const newData = new AttachmentData(attachmentData);
          const location = response.headers.get('location');
          if (location) {
            newData.Id = this.getAttachmentIdFromLocation(location);
          }
          if (!newData.ModifiedOn) {
            newData.ModifiedOn = newData.CreatedOn;
            newData.Modifier = newData.Creator;
          }
          return newData;
        }),
        catchError((e) => this.handleError(e))
    );

  }

  // upload attachment to group
  addAttachmentToGroup(groupUid: string, attachmentData: AttachmentData, file: File, forceNoThumbnail: boolean = false): Observable<AttachmentData> {
    const endpoint = this.getGroupAttachmentUrl(groupUid);
    const formData = this.createFormData(attachmentData, file);
    let httpParams = new HttpParams();
    httpParams = httpParams.set('type', GroupTypeEnum.MultiActivityInsert);
    httpParams = httpParams.set('forceNoThumbnail', forceNoThumbnail);

    return this.httpClient.post<any>(endpoint, formData, { headers: this.getHeaders(true), observe: 'response', params: httpParams })
    .pipe(
        map( response => {
          const newData = new AttachmentData(attachmentData);
          const location = response.headers.get('location');
          if (location) {
            newData.Id = this.getAttachmentIdFromLocation(location);
          }
          if (!newData.ModifiedOn) {
            newData.ModifiedOn = newData.CreatedOn;
            newData.Modifier = newData.Creator;
          }
          return newData;
        }),
        catchError((e) => this.handleError(e))
    );

  }
  private createFormData(attachmentData: AttachmentData, file: File): FormData {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    if (attachmentData.SUid) {
      formData.append('file_suid', attachmentData.SUid);
    }
    if (attachmentData.Description) {
      formData.append('file_description', attachmentData.Description);
    }
    if (attachmentData.Comment) {
      formData.append('file_comment', attachmentData.Comment);
    }
    if (attachmentData.Date) {
      // api can't format iso date with milliseconds
      const isoDate = attachmentData.Date.toISOString().slice(0, -5) + 'Z';
      formData.append('file_date', isoDate);
    } else if (file.lastModified) {
      // api can't format iso date with milliseconds
      const isoDate = new Date(file.lastModified).toISOString().slice(0, -5) + 'Z';
      formData.append('file_date', isoDate);
    }

    if (attachmentData.VisibleFor) {
      formData.append('file_visiblefor', attachmentData.VisibleFor.toString());
    }
    if (attachmentData.VisibleFor) {
      formData.append('file_editablefor', attachmentData.EditableFor.toString());
    }
    if (attachmentData.DocumentType && attachmentData.DocumentType.Id) {
      formData.append('file_document_type_id', attachmentData.DocumentType.Id.toString());
    }
    if (attachmentData.UniqueId) {
      formData.append('file_unique_id', attachmentData.UniqueId);
    }
    if (attachmentData.Type) {
      formData.append('file_type', attachmentData.Type.toString());
    }


    return formData;
  }

  // upload attachment to fault
  addAttachmentToFault(faultUId: string, attachmentData: AttachmentData, file: File, forceNoThumbnail: boolean = false): Observable<AttachmentData> {
    const endpoint = this.getFaultAttachmentUrl(faultUId);
    const formData = this.createFormData(attachmentData, file);

    let httpParams = new HttpParams();
    httpParams = httpParams.set('forceNoThumbnail', forceNoThumbnail);

    return this.httpClient.post<any>(endpoint, formData, { headers: this.getHeaders(true), observe: 'response', params: httpParams })
          .pipe(
              map( response => {
                const newData = new AttachmentData(attachmentData);
                const location = response.headers.get('location');
                if (location) {
                  newData.Id = this.getAttachmentIdFromLocation(location);
                }
                if (!newData.ModifiedOn) {
                  newData.ModifiedOn = newData.CreatedOn;
                  newData.Modifier = newData.Creator;
                }
                return newData;
              }),
              catchError((e) => this.handleError(e))
          );
  }

  // updateFaultAttachment(faultId: number, attachmentId: number, attachmentData: AttachmentData): Observable<AttachmentData> {
  //   const endpoint = `${super.getFullUrl(this.attachmentUrl)}/${attachmentId}`;
  //   return this.httpClient.put<any>(endpoint, attachmentData, { headers: this.getHeaders(true) })
  //         .pipe(
  //             map( at => {
  //               return at;
  //             }),
  //             catchError((e) => this.handleError(e))
  //         );
  // }
  //
  // updateActivityAttachment(activityId: number, attachmentId: number, attachmentData: AttachmentData): Observable<AttachmentData> {
  //   const endpoint = `${super.getFullUrl(this.attachmentUrl)}/${attachmentId}`;
  //   return this.httpClient.put<any>(endpoint, attachmentData, { headers: this.getHeaders(true) })
  //         .pipe(
  //             map( at => {
  //               return at;
  //             }),
  //             catchError((e) => this.handleError(e))
  //         );
  // }

  async updateAttachment(attachmentUId: string, attachmentData: AttachmentData): Promise<Observable<AttachmentData>> {
    const id = ToId(attachmentUId);

    /* TEST */

    // if (!attachmentId) {
    //     const endpoint = `${super.getFullUrl(this.attachmentUrl)}/${id}/metadata`;
    //     const loaded = await firstValueFrom(this.httpClient.get<AttachmentData>(endpoint, { headers: this.getHeaders() })
    //         .pipe(
    //             map( at => {
    //                 return new AttachmentData(at);
    //             }),
    //             catchError((e) => this.handleError(e))
    //         ));
    //
    //     attachmentId = loaded.Id;
    // }

    const endpoint = `${super.getFullUrl(this.attachmentUrl)}/${id}`;
    return this.httpClient.put<any>(endpoint, attachmentData, { headers: this.getHeaders(true) })
      .pipe(
        map( at => {
          return new AttachmentData(at);
        }),
        catchError((e) => this.handleError(e))
      );
  }

  deleteAttachments(attachments: Attachment[]) {
    const endpoint = this.getAttachmentUrl();
    const attachmentIds = [];
    attachments.forEach(att => attachmentIds.push(att.id));

    return this.httpClient.delete(endpoint, {
      body: attachmentIds,
      headers: this.getHeaders()
    }).pipe(
      map( modifiedOn => {
        if (modifiedOn?.toString()) {
          return new Date(modifiedOn?.toString());
        }
        return;
      }),
      catchError((e) => this.handleError(e)));
  }

  // Gets id from location (https://api-dev.edr-software.com/mm/2.6/Attachments/73268)
  private getAttachmentIdFromLocation(location: string): number {
    const index = location.toLowerCase().lastIndexOf(this.attachmentUrl.toLowerCase());
    const id =  location.substring(index + this.attachmentUrl.length + 1);
    return +id;
  }



}
